/**
 * AVBP payment-mode policy — the money-gate enforcement switch.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (owner directive 2026-05-19 — relax money-before-delivery into a
 *  CONFIGURABLE policy)
 * ─────────────────────────────────────────────────────────────────────────
 *  The locked principle was "every stage's fee is collected BEFORE that
 *  stage's deliverable leaves our hands" (memory project_money_before_
 *  delivery), enforced hard at two choke points: `canEnterStage`
 *  (engagementTemplate.ts) and `assertStageEntry` (projectLedger.ts).
 *
 *  The owner has EXPLICITLY relaxed this into a per-org-default +
 *  per-project-override policy:
 *    • 'payment_before' — the current hard behaviour (money gate blocks
 *      stage entry until paid). This stays the DEFAULT so nothing changes
 *      unless an admin opts a project (or the org) into work-first.
 *    • 'work_first'     — work proceeds; the money is still TRACKED (the
 *      gate, its amount, due/paid state are all still recorded and shown)
 *      but it NEVER blocks stage entry or delivery.
 *
 *  Resolution precedence: a project's own `paymentMode` field wins; if it
 *  is absent/unset the GLOBAL default (firm/settings doc) applies; if that
 *  too is absent we fall back to 'payment_before' (the safe, locked
 *  historical behaviour — work-first must be an explicit choice).
 *
 *  SCOPE OF THE RELAXATION: work-first relaxes the MONEY gate only.
 *  Curation gates (assignee/admin sign-off — memory
 *  project_curation_gating_assignee_model) and consultation gates are
 *  quality/authorisation gates, NOT payment, and STILL block. Work-first
 *  means "don't withhold work for money", not "skip review".
 *
 *  RULES CEILING (no firestore.rules change):
 *    • The global default lives at `settings/avbp_payment` — `settings/` is
 *      already team-readable/writable in the live rules (the same block
 *      workingCalendar.ts notes for `settings/orgHolidays`). Admin-gated in
 *      the UI (route guard), not by a new rule.
 *    • The per-project override is a field on the existing `projects/{pid}`
 *      doc, written through the SAME isTeam()-allowed `updateDoc` path
 *      projectStore.updateProject already uses (rule ~716). No new rule.
 *
 *  PURE/IMPURE split (mirrors staffRoles.ts / projectStore.ts):
 *  `isPaymentMode`, `resolvePaymentMode`, `moneyGateBlocks`,
 *  `paymentSettingsPatchBody`, `projectPaymentModePatchBody` are PURE +
 *  unit-tested. Only `listenPaymentSettings` / `setGlobalPaymentMode` /
 *  `setProjectPaymentMode` touch Firestore.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

/* ════════════════════ The policy vocabulary ════════════════════ */

export type PaymentMode = 'payment_before' | 'work_first';

/**
 * The SAFE, locked default. If neither the project nor the org has an
 * explicit mode, behaviour is exactly the historical money-before-delivery
 * hard gate. Work-first is always an explicit opt-in, never a silent one.
 */
export const DEFAULT_PAYMENT_MODE: PaymentMode = 'payment_before';

export interface PaymentModeOption {
  mode: PaymentMode;
  label: string;
  blurb: string;
}

/** Human labels + the real behavioural meaning of each mode. */
export const PAYMENT_MODE_OPTIONS: PaymentModeOption[] = [
  {
    mode: 'payment_before',
    label: 'Payment before delivery',
    blurb:
      'A stage cannot be entered until its money gate is paid. The locked ' +
      'money-before-delivery behaviour. Curation/quality gates also apply.',
  },
  {
    mode: 'work_first',
    label: 'Work first (payment tracked, not enforced)',
    blurb:
      'Work proceeds without waiting for payment. Amounts due/paid are ' +
      'still tracked and shown — they just never block a stage. Curation ' +
      'and quality gates still apply (work-first relaxes money only).',
  },
];

/* ════════════════════ Pure resolution ════════════════════ */

/** PURE — type guard. Anything not a known mode is treated as "unset". */
export function isPaymentMode(v: unknown): v is PaymentMode {
  return v === 'payment_before' || v === 'work_first';
}

/**
 * PURE — the EFFECTIVE mode for a project. Precedence:
 *   1. the project's own paymentMode (explicit per-project override)
 *   2. the global/org default (settings/avbp_payment.paymentMode)
 *   3. DEFAULT_PAYMENT_MODE ('payment_before' — the locked behaviour)
 * An unknown/garbage value at any level is ignored (falls through).
 */
export function resolvePaymentMode(
  projectMode: unknown,
  globalMode: unknown,
): PaymentMode {
  if (isPaymentMode(projectMode)) return projectMode;
  if (isPaymentMode(globalMode)) return globalMode;
  return DEFAULT_PAYMENT_MODE;
}

/**
 * PURE — the single predicate the gate choke points consult: does a MONEY
 * gate block under this effective mode? `false` under work-first (money is
 * tracked, never blocking); `true` under payment-before (locked behaviour).
 * Curation/consultation gates do NOT call this — they always block.
 */
export function moneyGateBlocks(mode: PaymentMode): boolean {
  return mode !== 'work_first';
}

/** PURE — human one-liner for a mode (UI badges / project header). */
export function paymentModeLabel(mode: PaymentMode): string {
  return (
    PAYMENT_MODE_OPTIONS.find((o) => o.mode === mode)?.label ?? mode
  );
}

/* ════════════════════ Pure write bodies (bounded, rules-safe) ════════════════════ */

/**
 * PURE — the EXACT body the global-default write sends to
 * `settings/avbp_payment`. ONLY { paymentMode, audit stamp } so the write
 * is minimal and can never smuggle another settings field. Throws on a bad
 * mode so an invalid value can never reach Firestore. `updatedAt` is filled
 * with serverTimestamp by the impure caller.
 */
export function paymentSettingsPatchBody(
  mode: PaymentMode,
  actorName: string,
): Record<string, unknown> {
  if (!isPaymentMode(mode)) {
    throw new Error(`Not a valid payment mode: "${String(mode)}"`);
  }
  return { paymentMode: mode, paymentModeUpdatedBy: actorName };
}

/**
 * PURE — the EXACT field set an updateProject write sends for a per-project
 * override. Only `paymentMode` + audit stamp; never touches any other
 * project field (cannot disturb gateState/tierCode/assignee). Throws on a
 * bad mode. `updatedAt` is filled impurely.
 */
export function projectPaymentModePatchBody(
  mode: PaymentMode,
  actorName: string,
): Record<string, unknown> {
  if (!isPaymentMode(mode)) {
    throw new Error(`Not a valid payment mode: "${String(mode)}"`);
  }
  return { paymentMode: mode, paymentModeUpdatedBy: actorName };
}

/* ════════════════════ Firestore seam (impure) ════════════════════ */

/**
 * Live realtime read of the org default (`settings/avbp_payment`). The doc
 * may not exist yet → callback gets DEFAULT_PAYMENT_MODE (locked behaviour),
 * never undefined. `settings/` team read is already allowed by the live
 * rules; the SETTINGS UI is admin route-gated.
 */
export function listenPaymentSettings(
  cb: (mode: PaymentMode) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'settings', 'avbp_payment'),
    (snap) => {
      const raw = snap.exists() ? snap.data().paymentMode : undefined;
      cb(isPaymentMode(raw) ? raw : DEFAULT_PAYMENT_MODE);
    },
    (e) => onErr?.(e),
  );
}

/**
 * Write the org-default payment mode. `setDoc(..., { merge:true })` so the
 * doc is created on first use without clobbering any future sibling
 * settings. Minimal body (paymentMode + audit) — rules-safe team write.
 */
export async function setGlobalPaymentMode(
  mode: PaymentMode,
  actorName: string,
): Promise<void> {
  await setDoc(
    doc(db, 'settings', 'avbp_payment'),
    {
      ...paymentSettingsPatchBody(mode, actorName),
      paymentModeUpdatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Write a per-project override onto the existing `projects/{pid}` doc via
 * the SAME isTeam()-allowed updateDoc path projectStore uses. Minimal body
 * so it never disturbs gateState/tier/assignee.
 */
export async function setProjectPaymentMode(
  pid: string,
  mode: PaymentMode,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    ...projectPaymentModePatchBody(mode, actorName),
    updatedAt: serverTimestamp(),
  });
}
